import { Form, FormProps } from "@/components/form";
import { CollapsibleSection } from "@/components/form/collapsible-section";
import { ConfirmDialog, useOpenModal } from "@/components/modal";
import { Grid2 as Grid, Stack } from "@mui/material";
import { z } from "zod/v4";
import { useState, useRef } from "react";
import DEArticuloSearch, { DetalleDE } from "../documento-electronico-form/de-articulo-search";
import DEArticuloList from "../documento-electronico-form/de-articulo-list";
import DocumentoAsociadoFields from "../documento-electronico-form/documento-asociado-fields";
import NotaCreditoGeneralInfoFields from "./nota-credito-general-info-fields";
import { notaCreditoFormSchema } from "../../schemas";
import { Cliente } from "@/shared/types/cliente";

export { notaCreditoFormSchema };

interface NotaCreditoFormProps extends FormProps<z.input<typeof notaCreditoFormSchema>> {
    onClienteChange?: (cliente: Cliente | null) => void;
    cliente?: Cliente | null;
    cdcLoaded: boolean;
    setCdcLoaded: (loaded: boolean) => void;
}

export function NotaCreditoForm({
    form,
    onClienteChange,
    cliente,
    cdcLoaded,
    setCdcLoaded,
    ...props
}: NotaCreditoFormProps) {
    const openModal = useOpenModal();
    const detalleFormRef = useRef<any>(null);
    const [showClienteError, setShowClienteError] = useState(false);

    const handleFormSubmit = () => {
        if (!cliente) {
            setShowClienteError(true);
            return false;
        }
        setShowClienteError(false);
        return true;
    };

    const handleReset = () => {
        form.reset();
        onClienteChange?.(null); // Reset 
        form.setFieldValue("clienteId", "");
        form.setFieldValue("tipoTransaccion", "2");
        form.setFieldValue("motivoEmision", "1");
        form.setFieldValue("tipoDocumentoAsociado", "1");
        form.setFieldValue("items", []);
        setShowClienteError(false);
        setCdcLoaded(false);
    };

    return <Form form={form} gap={2} {...props}>
        <NotaCreditoGeneralInfoFields
            detalleFormRef={detalleFormRef}
            onClienteChange={onClienteChange}
            cliente={cliente}
            clienteError={showClienteError}
            cdcLoaded={cdcLoaded}
        />
        <DocumentoAsociadoFields
            onClienteLoad={onClienteChange}
            cdcLoaded={cdcLoaded}
            setCdcLoaded={setCdcLoaded}
            handleReset={handleReset}
            forNotaCredito={true}
        />
        <CollapsibleSection title="Items">
            <Grid container columns={{ xs: 1, md: 3 }} spacing={2} flex={1}>
                <Grid component={Stack} size={1}>
                    <DEArticuloSearch
                        ref={detalleFormRef}
                        disabled={cdcLoaded}
                        onArticuloSelect={(v: DetalleDE) => {
                            const items = form.getFieldValue("items");
                            const index = items.findIndex(i => i.descripcion === v.descripcion)
                            if (index != -1) {
                                const previousValue = items[index];
                                const newValue = { ...previousValue, cantidad: previousValue.cantidad + v.cantidad }
                                form.replaceFieldValue("items", index, newValue);
                            } else {
                                form.pushFieldValue("items", {
                                    codigo: v.codigo,
                                    descripcion: v.descripcion,
                                    cantidad: v.cantidad,
                                    precio: v.precio,
                                    iva: v.iva,
                                    unidadMedida: v.unidadMedida
                                });
                            }
                        }} />
                </Grid>
                <Grid component={Stack} size={2} gap={2}>
                    <form.Field name="items" mode="array">
                        {field => <>
                            <DEArticuloList
                                items={(field.state.value || []) as DetalleDE[]}
                                remove={field.removeValue}
                                disabled={cdcLoaded}
                            />
                        </>}
                    </form.Field>
                </Grid>
            </Grid>
        </CollapsibleSection>
        <Stack direction={"row"} justifyContent={"end"} gap={2}>
            <Form.Button onClick={() => openModal("confirmReset")}>Cancelar</Form.Button>
            <Form.SubmitButton onClick={handleFormSubmit}>Confirmar</Form.SubmitButton>
        </Stack>

        <ConfirmDialog code="confirmReset">
            <ConfirmDialog.Content
                title="Cancelar Nota de Crédito"
                description="¿Estas seguro que quieres cancelar la nota de crédito actual?"
                onConfirm={handleReset}
            />
        </ConfirmDialog>
    </Form>
}
